export {
  HALCY_WAVE_PATH,
  HALCY_WAVE_FAVICON_PATH,
  HALCY_RING_PATH,
  HALCY_MARK_PATH,
  HALCY_MARK_FIELD,
  HALCY_MARK_INK,
} from './halcy-mark';

/**
 * @marketing-optimizer/themes
 *
 * Theme presets and utilities for healthcare SEO platform
 */

/**
 * WCAG 2.1 contrast helpers (HAL-33).
 *
 * These existed at `./lib/contrast-check` from the start but were never
 * exported here, so every consumer outside this package wrote its own. There
 * are FIVE private `contrastRatio` implementations today: the WordPress
 * contract spec, the cross-preset validation spec, the provider footer and
 * carousel guards, and `EngagementMetricRail.spec.tsx` in the dashboard. Five
 * copies of the same piecewise-gamma formula is five places for a rounding
 * difference to hide a real failure. Export it so the next guard reuses it
 * instead of becoming the sixth. Deduping the existing five is deliberately
 * NOT done here -- each is load-bearing for a different surface and rewriting
 * them all is its own change with its own risk.
 */
export {
  parseHex,
  relativeLuminance,
  contrastRatio,
  passesAA,
  passesAAA,
  // Extensionless, matching every other specifier in this barrel. The `.js`
  // form resolves under vitest but not under Next's webpack, which is what the
  // sibling spec files use and why copying their import style breaks the build.
} from './lib/contrast-check';

export {
  ThemeSchema,
  ThemeColorsSchema,
  ThemeColorsP3Schema,
  ThemeTypographySchema,
  ThemeSpacingSchema,
  ThemeBorderRadiusSchema,
  ButtonRadiusSchema,
  ThemeShadowsSchema,
  ThemeLayoutSchema,
  ThemeMotionSchema,
  ThemeDensitySchema,
  ThemeHeroSchema,
  FontPairingSchema,
  WideGamutColorSchema,
  ThemeOverridesSchema,
  ExtendedColorOverridesSchema,
  FontOverridesSchema,
  type Theme,
  type ThemeColors,
  type ThemeColorsP3,
  type ThemeTypography,
  type ThemeSpacing,
  type ThemeBorderRadius,
  type ButtonRadius,
  type ThemeShadows,
  type ThemeLayout,
  type ThemeMotion,
  type ThemeDensity,
  type ThemeHero,
  type FontPairing,
  type FontFaceFile,
  type WideGamutColor,
  type ThemeOverrides,
  type ExtendedColorOverrides,
  type FontOverrides,
} from './types';

// Font mapping utilities
export {
  mapFontToGoogleFonts,
  generateGoogleFontsUrl,
  resolveProviderFonts,
  isGoogleFont,
  normalizeFontName,
  findFontEntry,
  inferFontCategory,
  GOOGLE_FONTS,
  PROPRIETARY_FONTS,
  FONT_FALLBACK_MAP,
  DEFAULT_FALLBACKS,
  type FontEntry,
  type FontCategory,
  type FontStyle,
  type FontResolution,
} from './fonts';

export { baseTheme } from './presets/base';
export { minimalTheme } from './presets/minimal';
export { warmTheme } from './presets/warm';
export { professionalTheme } from './presets/professional';
export { clinicalTheme } from './presets/clinical';
export { wellnessTheme } from './presets/wellness';
export { editorialDarkTheme } from './presets/editorial-dark';
export { editorialLightTheme } from './presets/editorial-light';
export { cosmeticAtelierTheme } from './presets/cosmetic-atelier';

import {
  ThemeOverridesSchema,
  type Theme,
  type ThemeColorsP3,
  type ThemeOverrides,
  type FontPairing,
  type FontFaceFile,
} from './types';
import { mapFontToGoogleFonts } from './fonts';
import { baseTheme } from './presets/base';
import { minimalTheme } from './presets/minimal';
import { warmTheme } from './presets/warm';
import { professionalTheme } from './presets/professional';
import { clinicalTheme } from './presets/clinical';
import { wellnessTheme } from './presets/wellness';
import { editorialDarkTheme } from './presets/editorial-dark';
import { editorialLightTheme } from './presets/editorial-light';
import { cosmeticAtelierTheme } from './presets/cosmetic-atelier';

/**
 * Available theme presets
 *
 * RECOMMENDED for entity-page providers: Use 'base' theme with color overrides
 * from /detect-design-system. The base theme is the default for all new
 * provider onboarding.
 *
 * WEBSITE BUILDER presets (clinical, wellness, editorial-dark, editorial-light,
 * cosmetic-atelier): Active presets used by website builder section components.
 * Each bundles a complete `layout` token set (heroStyle, cardStyle, iconStyle,
 * buttonStyle, sectionPadding, contentMaxWidth) that drives section component
 * defaults. Do NOT treat these as deprecated.
 *
 * The legacy `cosmetic` preset was deprecated in GEN-1971; its infrastructure
 * (motion / density / hero tokens, fontPairings, asset-gate, image slots) was
 * generalized for `editorial-dark` / `editorial-light` / `cosmetic-atelier`.
 *
 * LEGACY presets (minimal, warm, professional): Maintained for backward
 * compatibility with existing provider sites. Not recommended for new providers.
 *
 * @see docs/engineering/2026-01-07-deprecation.md
 */
export const themePresets: Record<string, Theme> = {
  base: baseTheme,
  minimal: minimalTheme,
  warm: warmTheme,
  professional: professionalTheme,
  clinical: clinicalTheme,
  wellness: wellnessTheme,
  'editorial-dark': editorialDarkTheme,
  'editorial-light': editorialLightTheme,
  'cosmetic-atelier': cosmeticAtelierTheme,
};

/**
 * Get a theme by name with optional color overrides.
 *
 * When presetName is undefined or not provided, returns the base theme.
 * This is the RECOMMENDED pattern for new providers: omit preset and
 * provide color overrides from /detect-design-system.
 *
 * Provider overrides are authoritative over everything the preset ships:
 * - Any `colorsP3` entry the provider contradicts in sRGB is dropped, so the
 *   preset's wide-gamut block can never re-clobber a brand color (HAL-464).
 * - Supplying `fonts` drops the preset's `fontPairings`, which removes both
 *   the `@font-face` rules and the `<link rel="preload">` manifest for woff2
 *   files the provider doesn't ship. Set `fontPairing` to keep them.
 *
 * @param presetName - Optional theme preset name. Defaults to 'base' if omitted.
 * @param overrides - Optional color and font overrides to apply.
 * @returns The theme with overrides applied.
 *
 * @example
 * ```typescript
 * // New recommended pattern: base theme with detected colors
 * const theme = getTheme(undefined, {
 *   primaryColor: '#5C4A3D',
 *   accentColor: '#C4A777',
 * });
 *
 * // Legacy pattern: preset with overrides (still supported)
 * const theme = getTheme('warm', { primaryColor: '#5C4A3D' });
 * ```
 */
export function getTheme(
  presetName?: string,
  overrides?: ThemeOverrides
): Theme {
  // Default to base theme when no preset specified (recommended for new providers)
  const selectedTheme = presetName ? (themePresets[presetName] || baseTheme) : baseTheme;

  if (!overrides) {
    return selectedTheme;
  }

  // Validate overrides with schema (throws ZodError if invalid)
  const validatedOverrides = ThemeOverridesSchema.parse(overrides);

  // Apply color overrides
  const colors = { ...selectedTheme.colors };

  // Primary colors (auto-generate variants if not explicitly provided)
  if (validatedOverrides.primaryColor) {
    colors.primary = validatedOverrides.primaryColor;
    colors.primaryDark = validatedOverrides.primaryDarkColor || darkenColor(validatedOverrides.primaryColor, 20);
    colors.primaryLight = validatedOverrides.primaryLightColor || lightenColor(validatedOverrides.primaryColor, 20);
  } else {
    // Apply explicit dark/light overrides even without primary
    if (validatedOverrides.primaryDarkColor) colors.primaryDark = validatedOverrides.primaryDarkColor;
    if (validatedOverrides.primaryLightColor) colors.primaryLight = validatedOverrides.primaryLightColor;
  }

  // Accent colors (auto-generate variants if not explicitly provided)
  if (validatedOverrides.accentColor) {
    colors.accent = validatedOverrides.accentColor;
    colors.accentDark = validatedOverrides.accentDarkColor || darkenColor(validatedOverrides.accentColor, 20);
    colors.accentLight = validatedOverrides.accentLightColor || lightenColor(validatedOverrides.accentColor, 20);
  } else {
    // Apply explicit dark/light overrides even without accent
    if (validatedOverrides.accentDarkColor) colors.accentDark = validatedOverrides.accentDarkColor;
    if (validatedOverrides.accentLightColor) colors.accentLight = validatedOverrides.accentLightColor;
  }

  // Surface colors
  if (validatedOverrides.backgroundColor) colors.background = validatedOverrides.backgroundColor;
  if (validatedOverrides.backgroundAltColor) colors.backgroundAlt = validatedOverrides.backgroundAltColor;
  if (validatedOverrides.surfaceColor) colors.surface = validatedOverrides.surfaceColor;

  // Text colors
  if (validatedOverrides.textColor) colors.text = validatedOverrides.textColor;
  if (validatedOverrides.textMutedColor) colors.textMuted = validatedOverrides.textMutedColor;
  if (validatedOverrides.textInverseColor) colors.textInverse = validatedOverrides.textInverseColor;

  if (validatedOverrides.textInverseMutedColor) colors.textInverseMuted = validatedOverrides.textInverseMutedColor;

  // UI colors
  if (validatedOverrides.borderColor) colors.border = validatedOverrides.borderColor;

  // Preset-scoped tokens (HAL-464). Each is optional on both sides: an
  // override only lands when the provider supplies it, and `themeToCssVars()`
  // skips undefined values, so presets that never declared these emit nothing.
  if (validatedOverrides.goldMidColor) colors.goldMid = validatedOverrides.goldMidColor;
  if (validatedOverrides.primaryShade100Color) colors.primaryShade100 = validatedOverrides.primaryShade100Color;
  if (validatedOverrides.primaryShade200Color) colors.primaryShade200 = validatedOverrides.primaryShade200Color;
  if (validatedOverrides.primaryShade300Color) colors.primaryShade300 = validatedOverrides.primaryShade300Color;
  if (validatedOverrides.blushColor) colors.blush = validatedOverrides.blushColor;
  if (validatedOverrides.blushRuleColor) colors.blushRule = validatedOverrides.blushRuleColor;

  // Apply font overrides
  const typography = { ...selectedTheme.typography };

  if (validatedOverrides.fonts?.body) {
    // If font name already contains a comma, it's a full stack – pass through unchanged
    typography.fontFamily = resolveFontStack(validatedOverrides.fonts.body);
  }

  if (validatedOverrides.fonts?.heading) {
    typography.fontFamilyHeading = resolveFontStack(validatedOverrides.fonts.heading);
  }

  // Display / serif slots (HAL-464). Explicit overrides always win. When the
  // provider overrides only `heading`, the display + serif slots inherit that
  // stack – but ONLY if the preset already declared them. Presets without
  // those slots keep emitting nothing, so their CSS stays byte-identical.
  const headingFallback = validatedOverrides.fonts?.heading
    ? resolveFontStack(validatedOverrides.fonts.heading)
    : undefined;

  if (validatedOverrides.fonts?.display) {
    typography.fontFamilyDisplay = resolveFontStack(validatedOverrides.fonts.display);
  } else if (headingFallback && typography.fontFamilyDisplay) {
    typography.fontFamilyDisplay = headingFallback;
  }

  if (validatedOverrides.fonts?.serif) {
    typography.fontFamilySerif = resolveFontStack(validatedOverrides.fonts.serif);
  } else if (headingFallback && typography.fontFamilySerif) {
    typography.fontFamilySerif = headingFallback;
  }

  const fontPairings = resolveFontPairingsForOverrides(selectedTheme, validatedOverrides);
  const droppedPairing = selectedTheme.fontPairings && !fontPairings;

  return {
    ...selectedTheme,
    colors,
    colorsP3: reconcileColorsP3(selectedTheme.colorsP3, validatedOverrides),
    typography: droppedPairing
      ? repointOrphanedFontSlots(typography, selectedTheme, validatedOverrides)
      : applySelectedPairingFamilies(typography, selectedTheme, validatedOverrides),
    fontPairings,
    ...(validatedOverrides.buttonRadius !== undefined
      ? { buttonRadius: validatedOverrides.buttonRadius }
      : {}),
  };
}

/**
 * Resolve the selected preset's intended primary-button shape to a concrete
 * radius. Providers without a preset use the Part 2 default (6px).
 *
 * PRECEDENCE (HAL-618): an explicit `theme.buttonRadius` on the preset wins
 * over the coarser `layout.buttonStyle` bucket. `buttonStyle` only has three
 * values (pill / square / rounded) and drives the legacy generic `Button`
 * component; `buttonRadius` is an exact value that feeds `--button-radius`,
 * which is what the cosmetic-atelier CTA pill register actually reads. When a
 * preset declares both — as cosmetic-atelier does, keeping `buttonStyle: pill`
 * for legacy `Button` surfaces while shipping squared 4px CTAs — the exact
 * declaration is the truthful answer to "what radius do this preset's primary
 * CTAs render at", which is the question every caller here is asking.
 *
 * Without this, the HAL-509 CTA-radius evidence gate resolves a preset-source
 * artifact through `buttonStyle` and contradicts the radius the site actually
 * ships.
 */
export function naturalButtonRadius(presetName?: string): string {
  if (presetName === undefined) return '0.375rem';

  const theme = getTheme(presetName);
  if (theme.buttonRadius !== undefined) return theme.buttonRadius;

  switch (theme.layout?.buttonStyle) {
    case 'pill':
      return theme.borderRadius.full;
    case 'square':
      return theme.borderRadius.none;
    case 'rounded':
    default:
      return theme.borderRadius.md;
  }
}

/**
 * Point the typography slots at the families of an explicitly-named pairing.
 *
 * A pairing carries both the `@font-face` rules and the family stacks that
 * name the faces those rules load. Before HAL-578 only the former was wired
 * up: `themeToCssVars()` reads `theme.typography`, never the pairing, so
 * naming a non-default pairing emitted its woff2 while every heading kept
 * naming the DEFAULT pairing's family. The registered pairing was inert.
 *
 * Scoped deliberately to the explicit-selection path. On the `default: true`
 * fallthrough this is a no-op, so every provider that names no pairing emits
 * byte-identical CSS to before HAL-578 — structurally, not by luck.
 *
 * Explicit `fonts.*` overrides still win. A provider that sets both
 * `fonts.heading` and `fontPairing` keeps its own heading family and takes
 * only the `@font-face` rules and preload manifest from the pairing, which is
 * the HAL-464 escape hatch working as documented. Slot claiming mirrors the
 * override block above: a `fonts.heading` override claims the display and
 * serif slots too, because they inherit it there.
 *
 * `FontPairingSchema` has no `fontFamilySerif`, so the serif slot follows the
 * pairing's heading family — on every preset declaring a serif slot, that slot
 * IS the display serif.
 */
function applySelectedPairingFamilies(
  typography: Theme['typography'],
  theme: Theme,
  overrides: ThemeOverrides,
): Theme['typography'] {
  const requestedKey = overrides.fontPairing;
  if (!requestedKey) return typography;

  // Exact-key lookup only. An unknown key must NOT silently fall through to
  // the default pairing's families here, because `resolveFontPairing()` would
  // then load the default's woff2 while these stacks named something else.
  const pairing = theme.fontPairings?.[requestedKey];
  if (!pairing) return typography;

  const headingClaimed = Boolean(overrides.fonts?.heading);
  const next = { ...typography };

  if (!overrides.fonts?.body) {
    next.fontFamily = pairing.fontFamilyBody;
  }
  if (!headingClaimed) {
    next.fontFamilyHeading = pairing.fontFamilyHeading;
  }
  // Display and serif only when the preset already declared the slot, so
  // presets omitting them keep emitting no `--font-family-{display,serif}`.
  if (!overrides.fonts?.display && !headingClaimed && next.fontFamilyDisplay) {
    next.fontFamilyDisplay = pairing.fontFamilyDisplay;
  }
  if (!overrides.fonts?.serif && !headingClaimed && next.fontFamilySerif) {
    next.fontFamilySerif = pairing.fontFamilyHeading;
  }

  return next;
}

/**
 * Repoint any typography slot still naming a family the dropped pairing owned.
 *
 * Dropping `fontPairings` removes the ONLY `@font-face` source for the
 * preset's self-hosted faces. A partial override (say `fonts.body` alone on
 * cosmetic-atelier) would otherwise leave `fontFamilyHeading` /
 * `fontFamilyDisplay` / `fontFamilySerif` still naming Cormorant Garamond
 * with nothing left to load it – every heading on the site would silently
 * fall through to the stack's system-serif tail. Repointing each orphaned
 * slot onto a family the provider actually supplied keeps the invariant
 * simple: after `getTheme()`, no emitted font stack names a face the build
 * does not ship.
 *
 * Precedence is nearest-in-tier first: display and serif are heading-tier, so
 * they prefer a heading override before falling back to body.
 */
function repointOrphanedFontSlots(
  typography: Theme['typography'],
  selectedTheme: Theme,
  overrides: ThemeOverrides,
): Theme['typography'] {
  const pairingFamilies = Object.values(selectedTheme.fontPairings ?? {})
    .flatMap((pairing) => pairing.files.map((file) => file.family));
  if (pairingFamilies.length === 0) return typography;

  const namesPairingFamily = (stack: string | undefined): boolean =>
    stack !== undefined && pairingFamilies.some((family) => stack.includes(family));

  const stack = (value: string | undefined): string | undefined =>
    value === undefined ? undefined : resolveFontStack(value);

  const body = stack(overrides.fonts?.body);
  const heading = stack(overrides.fonts?.heading);
  const display = stack(overrides.fonts?.display);
  const serif = stack(overrides.fonts?.serif);

  // At least one of these is defined – a pairing is only dropped when the
  // provider supplied a family – so every `??` chain below resolves.
  const next = { ...typography };
  if (namesPairingFamily(next.fontFamily)) {
    next.fontFamily = body ?? heading ?? display ?? serif ?? next.fontFamily;
  }
  if (namesPairingFamily(next.fontFamilyHeading)) {
    next.fontFamilyHeading = heading ?? display ?? serif ?? body ?? next.fontFamilyHeading;
  }
  if (namesPairingFamily(next.fontFamilyDisplay)) {
    next.fontFamilyDisplay = display ?? heading ?? serif ?? body ?? next.fontFamilyDisplay;
  }
  if (namesPairingFamily(next.fontFamilySerif)) {
    next.fontFamilySerif = serif ?? heading ?? display ?? body ?? next.fontFamilySerif;
  }
  return next;
}

/**
 * Resolve a font override value to a full CSS font stack.
 *
 * A value containing a comma is already a stack and passes through unchanged;
 * a bare family name is expanded via the Google Fonts fallback map.
 */
function resolveFontStack(value: string): string {
  return value.includes(',') ? value : mapFontToGoogleFonts(value).fontStack;
}

/**
 * Map each sRGB override field to the `colorsP3` key it would contradict.
 *
 * `themeToCss()` emits the P3 block AFTER the `:root` sRGB block, so any P3
 * entry the preset declares WINS on wide-gamut displays. That silently undid
 * provider color overrides on exactly the hardware clients review their site
 * on (HAL-464). Dropping the contradicted entries keeps P3 available as a
 * preset feature while making the provider override the authority.
 */
// Keyed by `ThemeColorsP3` (not by override field) and NOT `Partial`, so
// adding a token to `ThemeColorsP3Schema` without mapping it here is a compile
// error rather than a silent hole that reopens the HAL-464 bug for that token.
const P3_KEY_TO_OVERRIDE: Record<keyof ThemeColorsP3, keyof ThemeOverrides> = {
  primary: 'primaryColor',
  primaryDark: 'primaryDarkColor',
  primaryLight: 'primaryLightColor',
  accent: 'accentColor',
  accentDark: 'accentDarkColor',
  accentLight: 'accentLightColor',
  background: 'backgroundColor',
  backgroundAlt: 'backgroundAltColor',
  surface: 'surfaceColor',
  text: 'textColor',
  textMuted: 'textMutedColor',
  textInverse: 'textInverseColor',
  border: 'borderColor',
};

function reconcileColorsP3(
  colorsP3: ThemeColorsP3 | undefined,
  overrides: ThemeOverrides,
): ThemeColorsP3 | undefined {
  if (!colorsP3) return undefined;

  const next: ThemeColorsP3 = { ...colorsP3 };
  for (const [p3Key, overrideKey] of Object.entries(P3_KEY_TO_OVERRIDE) as Array<
    [keyof ThemeColorsP3, keyof ThemeOverrides]
  >) {
    if (overrides[overrideKey] !== undefined) {
      delete next[p3Key];
    }
  }

  // `primaryColor` / `accentColor` also auto-generate the dark + light sRGB
  // variants, so their P3 alternates must go too – otherwise the generated
  // sRGB variant and the preset's P3 variant disagree on wide-gamut.
  if (overrides.primaryColor !== undefined) {
    delete next.primaryDark;
    delete next.primaryLight;
  }
  if (overrides.accentColor !== undefined) {
    delete next.accentDark;
    delete next.accentLight;
  }

  return Object.values(next).some((value) => value !== undefined) ? next : undefined;
}

/**
 * Drop the preset's `fontPairings` when the provider brings its own faces.
 *
 * A pairing carries `@font-face` rules AND the `<link rel="preload">` manifest
 * for self-hosted woff2 files that live under the PRESET's provider directory.
 * A second provider on the same preset won't ship those files, so keeping the
 * pairing means two 404s and a Lighthouse warning on every page (HAL-464).
 *
 * An explicit `fontPairing` selection means the provider WANTS a preset
 * pairing, so the pairings survive even alongside family overrides.
 */
function resolveFontPairingsForOverrides(
  theme: Theme,
  overrides: ThemeOverrides,
): Theme['fontPairings'] {
  if (!theme.fontPairings) return undefined;
  if (overrides.fontPairing) return theme.fontPairings;

  const overridesFontFamily = Boolean(
    overrides.fonts?.body ||
      overrides.fonts?.heading ||
      overrides.fonts?.display ||
      overrides.fonts?.serif,
  );

  return overridesFontFamily ? undefined : theme.fontPairings;
}

/**
 * Generate CSS custom properties from a theme.
 *
 * Emits four optional token blocks when present on the theme:
 * - `motion` -> `--motion-*` (durations, easings, distances, parallax ceiling)
 * - `density` -> `--density-*` (homepage section gaps, max-widths, gutters)
 * - `hero` -> `--hero-*` (aspect ratios, mark/text origins, scrim, fade duration)
 * - `colorsP3` -> ignored here; use `themeToCss()` to emit the `@supports` block
 *
 * P3 color alternates are NOT emitted as flat `--color-*-p3` variables because
 * browsers without P3 support cannot read them. Instead, `themeToCss()` wraps
 * P3 declarations in `@supports (color: color(display-p3 0 0 0))` so the
 * cascade falls through cleanly on legacy browsers.
 */
export function themeToCssVars(theme: Theme): Record<string, string> {
  const vars: Record<string, string> = {};

  // Colors (sRGB defaults). Optional color tokens (e.g., `goldMid`) only emit
  // when the preset sets them; presets that omit the field leave the CSS var
  // out entirely so non-cosmetic-atelier presets render byte-identically.
  for (const [key, value] of Object.entries(theme.colors)) {
    if (value === undefined) continue;
    vars[`--color-${camelToKebab(key)}`] = value;
  }

  // Typography
  for (const [key, value] of Object.entries(theme.typography)) {
    vars[`--${camelToKebab(key)}`] = value;
  }

  // Spacing
  for (const [key, value] of Object.entries(theme.spacing)) {
    vars[`--spacing-${key}`] = value;
  }

  // Border radius
  for (const [key, value] of Object.entries(theme.borderRadius)) {
    vars[`--radius-${key}`] = value;
  }

  if (theme.buttonRadius !== undefined) {
    vars['--button-radius'] = theme.buttonRadius;
  }

  // Shadows
  for (const [key, value] of Object.entries(theme.shadows)) {
    vars[`--shadow-${key}`] = value;
  }

  // Layout (website builder tokens – optional)
  if (theme.layout) {
    for (const [key, value] of Object.entries(theme.layout)) {
      vars[`--layout-${camelToKebab(key)}`] = value;
    }
  }

  // Motion tokens (cosmetic preset – optional)
  if (theme.motion) {
    for (const [key, value] of Object.entries(theme.motion)) {
      vars[`--motion-${camelToKebab(key)}`] = value;
    }
  }

  // Density tokens (cosmetic preset – optional)
  if (theme.density) {
    for (const [key, value] of Object.entries(theme.density)) {
      // Numeric tokens (e.g., homeMaxSections) emit as bare numbers; CSS treats them as <number>.
      vars[`--density-${camelToKebab(key)}`] = String(value);
    }
  }

  // Hero CSS hooks (cosmetic preset – optional)
  if (theme.hero) {
    for (const [key, value] of Object.entries(theme.hero)) {
      vars[`--hero-${camelToKebab(key)}`] = value;
    }
  }

  return vars;
}

/**
 * Generate CSS string from theme.
 *
 * Emits, in order:
 * 1. `@font-face` declarations + `<link rel="preload">`-equivalent CSS hint
 *    comments for the active font pairing (when present).
 * 2. `:root { ... }` with the sRGB token block (from `themeToCssVars`).
 * 3. An `@supports (color: color(display-p3 0 0 0))` block with the P3
 *    overrides (when `theme.colorsP3` is present).
 * 4. A reduced-motion reset that collapses motion tokens to zero (cosmetic
 *    preset Charter MR-2). Emitted only when `theme.motion` is present.
 * 5. A global `:focus-visible` outline rule (GEN-1778) so keyboard users see
 *    a clear focus indicator on every interactive element. WCAG 2.1 §2.4.7
 *    requires a visible focus indicator; the default browser ring is
 *    inconsistent across themes (especially low-contrast on cream/sage
 *    backgrounds) so we standardize on the theme's primary color.
 *
 * Item 1 is skipped when the theme has no `fontPairings`. Item 3 is skipped
 * when the theme has no `colorsP3`. Item 4 is skipped when the theme has
 * no `motion` block. This keeps non-cosmetic presets unchanged.
 */
export function themeToCss(theme: Theme, options?: { fontPairing?: string }): string {
  const blocks: string[] = [];

  // 1. @font-face declarations from the active pairing (if any)
  const activePairing = resolveFontPairing(theme, options?.fontPairing);
  if (activePairing) {
    blocks.push(renderFontFaceBlock(activePairing));
  }

  // 2. :root { ... } with sRGB tokens
  const vars = themeToCssVars(theme);
  const rootLines = Object.entries(vars).map(([key, value]) => `  ${key}: ${value};`);
  blocks.push(`:root {\n${rootLines.join('\n')}\n}`);

  // 3. P3 color overrides (if declared)
  if (theme.colorsP3) {
    const p3Entries = Object.entries(theme.colorsP3).filter(([, value]) => value !== undefined);
    if (p3Entries.length > 0) {
      const p3Lines = p3Entries.map(([key, value]) => `    --color-${camelToKebab(key)}: ${value};`);
      blocks.push(
        `@supports (color: color(display-p3 0 0 0)) {\n  :root {\n${p3Lines.join('\n')}\n  }\n}`,
      );
    }
  }

  // 4. Reduced-motion reset (Charter MR-2)
  if (theme.motion) {
    blocks.push(renderReducedMotionReset());
  }

  // 5. Keyboard focus indicator (GEN-1778, WCAG 2.1 §2.4.7). The outline is
  //    keyed off the theme's primary color so it adapts per preset; offset +
  //    border-radius:inherit make it sit cleanly around buttons / chips /
  //    pills without clipping. Falls back to the legacy 2px solid behavior
  //    if `:focus-visible` is unsupported via the `:focus:not(:focus-visible)`
  //    reset (suppresses the ring on mouse focus once keyboard support lands).
  blocks.push(
    [
      ':focus-visible {',
      '  outline: 2px solid var(--color-primary);',
      '  outline-offset: 2px;',
      '  border-radius: inherit;',
      '}',
      ':focus:not(:focus-visible) {',
      '  outline: none;',
      '}',
    ].join('\n'),
  );

  return blocks.join('\n\n');
}

/**
 * Resolve the active font pairing from a theme.
 *
 * Resolution order:
 * 1. Explicit `requestedKey` (when provided and present in `theme.fontPairings`)
 * 2. Pairing flagged with `default: true`
 * 3. First pairing in declaration order
 * 4. `null` when the theme has no `fontPairings`
 */
export function resolveFontPairing(theme: Theme, requestedKey?: string): FontPairing | null {
  if (!theme.fontPairings) return null;
  const entries = Object.entries(theme.fontPairings);
  if (entries.length === 0) return null;

  if (requestedKey && theme.fontPairings[requestedKey]) {
    return theme.fontPairings[requestedKey];
  }

  const explicitDefault = entries.find(([, pairing]) => pairing.default);
  if (explicitDefault) return explicitDefault[1];

  return entries[0][1];
}

function renderFontFaceBlock(pairing: FontPairing): string {
  const faces = pairing.files.map((file) => {
    const lines = [
      `  font-family: ${escapeFontFamily(file.family)};`,
      `  font-style: ${file.style};`,
      `  font-weight: ${file.weight};`,
      `  font-display: ${file.display};`,
      `  src: url("${file.url}") format("${file.format}");`,
    ];
    if (file.unicodeRange) {
      lines.push(`  unicode-range: ${file.unicodeRange};`);
    }
    return `@font-face {\n${lines.join('\n')}\n}`;
  });

  // Preload candidates are exposed structurally via `getPreloadManifest()`
  // rather than as a CSS comment – CSS minifiers (cssnano, esbuild, Next.js
  // production builds) routinely strip comments, so a comment-based contract
  // would silently break in production while passing in dev.
  return faces.join('\n\n');
}

/**
 * Return the preload candidates declared by the active font pairing.
 *
 * Each entry is the woff2 file marked `preload: true` in the pairing
 * manifest. Consumers (typically a Next.js root layout or a `Head`
 * component) emit these as `<link rel="preload" as="font" type="font/woff2"
 * crossorigin>` tags.
 *
 * Returns an empty array when the theme has no `fontPairings` or the active
 * pairing declares no preloaded files.
 */
export function getPreloadManifest(theme: Theme, requestedKey?: string): readonly FontFaceFile[] {
  const pairing = resolveFontPairing(theme, requestedKey);
  if (!pairing) return [];
  return pairing.files.filter((file) => file.preload);
}

function escapeFontFamily(family: string): string {
  // Wrap in quotes when family contains whitespace; otherwise emit bare.
  return /\s/.test(family) ? `"${family}"` : family;
}

function renderReducedMotionReset(): string {
  return [
    '@media (prefers-reduced-motion: reduce) {',
    '  *,',
    '  *::before,',
    '  *::after {',
    '    animation-duration: 0.01ms !important;',
    '    animation-iteration-count: 1 !important;',
    '    transition-duration: 0.01ms !important;',
    '  }',
    '  [data-parallax] { transform: none !important; }',
    '  .horizontal-scroll-gallery { scroll-snap-type: none !important; }',
    '}',
  ].join('\n');
}

// Utility functions

function camelToKebab(str: string): string {
  // Two-boundary kebab conversion:
  // - lower→Upper  (`primaryDark`       → `primary-Dark`)
  // - letter→digit (`primaryShade100`   → `primaryShade-100`,
  //                 `fontSize2xl`       → `fontSize-2xl`)
  //
  // Digit→letter is intentionally NOT a boundary so `fontSize2xl` lands as
  // `font-size-2xl` (consumer-expected form) rather than `font-size-2-xl`,
  // and so future tokens like `radius2x` stay readable as `radius-2x`.
  //
  // The letter→digit rule was added in GEN-2028 so the new
  // `primaryShade{100,200,300}` tokens publish as
  // `--color-primary-shade-{100,200,300}`. The same change harmonizes
  // `fontSize{2,3,4}xl` with the `--font-size-{2,3,4}xl` form that the
  // demo provider stylesheets already consume via `var()` (the prior
  // emission was `--font-size2xl`, which never resolved in consumers and
  // silently fell through to their inline fallbacks).
  return str
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .replace(/([a-zA-Z])(\d)/g, '$1-$2')
    .toLowerCase();
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

function darkenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = 1 - percent / 100;
  return rgbToHex(
    Math.round(rgb.r * factor),
    Math.round(rgb.g * factor),
    Math.round(rgb.b * factor)
  );
}

function lightenColor(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const factor = percent / 100;
  return rgbToHex(
    Math.round(rgb.r + (255 - rgb.r) * factor),
    Math.round(rgb.g + (255 - rgb.g) * factor),
    Math.round(rgb.b + (255 - rgb.b) * factor)
  );
}
